import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { NoAccess } from '@/components/custom/no-access';
import { UserEditForm } from '@/components/custom/user-edit-form';
import { PageShell } from '@/components/layout/page-shell';
import { useHasPermission } from '@/lib/permissions';

/**
 * `?version=N` turns this page into a HISTORICAL AMEND of that version rather
 * than an edit of the record (D10) — same form, seeded from what the version
 * says and submitted to the amend endpoint. See `UserEditForm`.
 */
const editSearch = z.object({
  version: z.coerce.number().int().positive().optional(),
});

export const Route = createFileRoute('/_authenticated/users/$id/edit')({
  component: UserEditPage,
  validateSearch: editSearch,
});

function UserEditPage() {
  const { id } = Route.useParams();
  const { version } = Route.useSearch();
  const canAmend = useHasPermission('user.admin');

  // Amending history is an admin-tier authority, separate from `user.edit`:
  // editing the record changes it from today, amending a version changes what
  // every report over that period has always said. The server enforces the
  // same gate; this keeps someone from filling in a form that can only 403.
  if (version != null && !canAmend) {
    return (
      <PageShell>
        <NoAccess permission="user.admin" resource="this record's history" />
      </PageShell>
    );
  }

  return <UserEditForm mode="edit" userId={id} amendVersion={version} />;
}
